import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Cpu, Network, Building2, HeartPulse, GraduationCap, Landmark, TrendingUp, Users, ShieldCheck, Globe2, ArrowRight, Plus, Equal } from "lucide-react";
import { Button } from "@/components/ui/button";

const EQUATION = [
  { icon: Network, name: "Care.com", role: "Marketplace" },
  { icon: Cpu, name: "WellSky", role: "B2B/B2G software, ops, analytics & AI" },
  { icon: HeartPulse, name: "Honor", role: "Tech-powered care delivery" },
  { icon: GraduationCap, name: "T5 Academy", role: "Training & workforce" },
];
const TAM = [
  ["$600B+", "U.S. care economy (home, facility & community)"],
  ["50 + DC", "Multi-state compliance & training coverage"],
  ["4", "Stakeholders unified on one platform"],
];
const PILLARS = [
  { icon: Network, t: "Marketplace", d: "Two-sided caregiver & opportunity network (B2C/B2B/B2G)." },
  { icon: Building2, t: "Workforce OS", d: "Scheduling, workforce analytics, compliance & AI for agencies and government." },
  { icon: HeartPulse, t: "Care delivery", d: "Digital intake, scheduling and family coordination." },
  { icon: GraduationCap, t: "Academy", d: "State-specific training, credentialing & CE that builds supply." },
];
const MOAT = [
  { icon: ShieldCheck, t: "50-state compliance engine", d: "A sourced, verified national rules engine — the hardest wedge to replicate." },
  { icon: Globe2, t: "National from day one", d: "Architected for every jurisdiction; Ohio is simply the first market." },
  { icon: TrendingUp, t: "Compounding data", d: "Every match, shift and course improves matching and forecasting." },
  { icon: Users, t: "Full-stack network", d: "Owning supply (Academy) + demand (families/agencies/gov) + delivery + software." },
];

export default function Investors() {
  const navigate = useNavigate();
  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3">Partners &amp; Investors · One-Pager</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-3xl leading-tight">The operating system for the U.S. care economy.</h1>
          <p className="mt-4 text-blue-100/85 max-w-2xl text-lg">T5 unifies the four categories of care — marketplace, operations software, care delivery and workforce training — into one national, AI-powered platform serving families, caregivers, care companies and government.</p>
        </div>
      </section>

      {/* Equation */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-wrap items-stretch justify-center gap-3" data-testid="investor-equation">
          {EQUATION.map((e, i) => (
            <div key={e.name} className="flex items-center gap-3">
              <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="w-44 bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm text-center">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700 mx-auto mb-3"><e.icon className="w-6 h-6" /></span>
                <p className="font-display font-bold text-blue-950 text-sm">{e.name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{e.role}</p>
              </motion.div>
              {i < EQUATION.length - 1 && <Plus className="w-5 h-5 text-slate-300 shrink-0" />}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <Equal className="w-6 h-6 text-slate-300" />
            <div className="w-44 bg-blue-950 text-white rounded-2xl p-5 shadow-lg text-center">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-500/20 text-teal-400 mx-auto mb-3"><Cpu className="w-6 h-6" /></span>
              <p className="font-display font-bold text-sm">T5 Care Platform</p>
              <p className="text-[11px] text-blue-200 mt-0.5">One care OS</p>
            </div>
          </div>
        </div>
      </section>

      {/* Market */}
      <section className="bg-slate-50 border-y border-blue-900/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-3 gap-4" data-testid="investor-market">
          {TAM.map(([v, l]) => (
            <div key={l} className="bg-white rounded-2xl p-6 border border-blue-900/5 text-center">
              <p className="font-display text-3xl font-bold text-teal-600">{v}</p>
              <p className="text-sm text-slate-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pillars + moat */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-2xl font-bold text-blue-950 mb-5">What we've built</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {PILLARS.map((p) => (
            <div key={p.t} className="bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700 mb-3"><p.icon className="w-6 h-6" /></span>
              <h3 className="font-display font-bold text-blue-950">{p.t}</h3>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
        <h2 className="font-display text-2xl font-bold text-blue-950 mb-5">Why it's defensible</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {MOAT.map((m) => (
            <div key={m.t} className="bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm flex gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-950 text-teal-400 shrink-0"><m.icon className="w-6 h-6" /></span>
              <div><h3 className="font-display font-bold text-blue-950">{m.t}</h3><p className="text-sm text-slate-500 mt-1 leading-relaxed">{m.d}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-blue-950 text-white p-10 text-center">
          <Landmark className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h2 className="font-display text-2xl lg:text-3xl font-bold">Let's build the future of care together.</h2>
          <p className="mt-2 text-blue-100/80">Partnership, investment and pilot inquiries welcome.</p>
          <Button onClick={() => navigate("/request-demo")} data-testid="investor-cta" className="mt-6 h-12 px-7 bg-teal-500 hover:bg-teal-600 text-white">Get in touch <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
        </div>
      </section>
    </div>
  );
}
